import { useState, useEffect } from "react";
import ReactFileUpload from "./ReactFileUpload";
import { Typography } from "@mui/material";
import * as React from "react";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { DocumentComponent } from "../../../features/document/components/document-component";
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import { baseUrl } from "../../../config/config";
import { fetchDocumentSections } from "../../../redux/slices/documentSectionsSlice";
import { DocumentServices } from "../../../features/document/services/document-services";
import {
  UploadDocument,
} from "../../../redux/slices/documentCommentSlice";
import { postApiCall } from "../../../services/apiHelper";
import { changeStatus } from "../../../redux/slices/onChangeSlice";
import { GenericToast } from "components/util/ToastMessage";
import { LoadingButton } from "@mui/lab";
import { resetPropertyImagesAction } from "../../../redux/slices/propertyImagesSlice";

interface toast {
  message: any;
  severity: string;
}

const Input = styled("input")({
  display: "none",
});
const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, .05)"
      : "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

interface DocumentsProps {
  mode: string;
}

function Documents(props: DocumentsProps) {
  const dispatch = useAppDispatch();
  const [submitLoaderButton, setSubmitLoaderButton] = useState(false);
  const [toastMessageOpen, setToastMessageOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<toast>({
    message: "",
    severity: "",
  });

  const dapData = useAppSelector((state) => state.summary.dapData);
  const isAnalyst = useAppSelector(
    (state) => state.userSectorDetail.data.isAnalyst
  );
    const isAdmin = useAppSelector(
      (state) => state.userSectorDetail.data.isAdmin
    );
  const roles = useAppSelector((state) => state.userSectorDetail.data.roles);
  const isAssetManager = roles?.includes("Asset Manager");
  const [isSectionValueEmpty, setIsSectionValueEmpty] =
    useState<boolean>(false);

  const assetPlanId = useAppSelector(
    (state) => state.getAssetPlanId.assetPlanId,
  );
  const [sectionValue, setSectionValue] = React.useState<string | null>();
  const documentSections = useAppSelector(
    (state) => state.documentSections.sections,
  );
  const documentSectionsLoading = useAppSelector(
    (state) => state.documentSections.loading,
  );
  const portfolioInvestments = useAppSelector((state) => state.abfStatus.data);
  const [selectedInvestmentId, setSelectedInvestmentId] = useState<string>("");

  const handleInvestmentIdChange = (event: SelectChangeEvent) => {
    setSelectedInvestmentId(event.target.value);
  };

  const documentServices = new DocumentServices();
  const [newUserInfo, setNewUserInfo] = useState({
    profileImages: [],
  });

  const updateUploadedFiles = (files) =>
    setNewUserInfo({ ...newUserInfo, profileImages: files });

  const [documentData, setDocumentData] = useState([]);

  const validateFileType = (
    files: File[],
    selectedSection: string | null | undefined,
  ) => {
    const imageOnlySections = ["Property Images", "Site Plan", "Stacking Plan"];
    const allowedExtensions = [
      ".pdf",
      ".ppt",
      ".pptx",
      ".zip",
      ".jpg",
      ".xlsx",
      ".xlsm",
      ".xlsb",
      ".xls",
      ".doc",
      ".docx",
      ".jpeg",
      ".gif",
      ".png",
      ".bmp",
      ".tiff",
      ".txt",
      ".msg",
      ".csv",
      ".webp",
      ".svg",
    ];

    if (selectedSection && imageOnlySections.includes(selectedSection)) {
      const imageExtensions = [
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
        ".bmp",
        ".webp",
        ".svg",
        ".tiff",
      ];
      const invalidFiles = files.filter((file) => {
        const fileExtension = file.name
          .toLowerCase()
          .substring(file.name.lastIndexOf("."));
        return !imageExtensions.includes(fileExtension);
      });

      if (invalidFiles.length > 0) {
        setToastMessageOpen(true);
        setToastMessage({
          message: `Only image files are allowed for ${selectedSection} section`,
          severity: "error",
        });
        setTimeout(() => {
          setToastMessageOpen(false);
        }, 4000);
        return false;
      }
    }

    const invalidExtFiles = files.filter((file) => {
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));
      return !allowedExtensions.includes(fileExtension);
    });

    if (invalidExtFiles.length > 0) {
      setToastMessageOpen(true);
      setToastMessage({
        message:
          "Only allowed file extensions: .pdf, .ppt, .pptx, .zip, .jpg, .xlsx, .xlsm, .xlsb, .xls, .doc, .docx, .jpeg, .gif, .png, .bmp, .tiff, .txt, .msg, .csv",
        severity: "error",
      });
      setTimeout(() => {
        setToastMessageOpen(false);
      }, 4000);
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    if (!validateFileType(newUserInfo.profileImages, sectionValue)) {
      return;
    }

    setSubmitLoaderButton(true);
    let sectionName = sectionValue ?? "";

    try {
      if (sectionValue != undefined) {
        if (newUserInfo.profileImages.length > 0) {
          setToastMessageOpen(false);

          for (const file of newUserInfo.profileImages as File[]) {
            const investmentId = dapData?.investmentId
              ?.toLowerCase()
              .includes("portfolio")
              ? selectedInvestmentId || ""
              : dapData?.investmentId || "";

            // Step 1: Get presigned URL from backend
            const presignedResponse: any = await postApiCall(
              `${baseUrl}Document/GetUploadPresignedUrl`,
              {
                assetPlanID: assetPlanId,
                investmentId: investmentId,
                sectionName: sectionName,
                budgetYear: dapData?.budgetYear != undefined ? dapData?.budgetYear : "",
                fileName: file.name,
                fileSize: file.size,
                contentType: file.type,
              }
            );

            // Step 2: Upload file directly to S3 using presigned URL
            await fetch(presignedResponse.url, {
              method: "PUT",
              headers: { "Content-Type": file.type },
              body: file,
            });
          }

          setSubmitLoaderButton(false);
          setToastMessageOpen(true);
          setToastMessage({
            message: "Saved successfully",
            severity: "success",
          });

          if (sectionName === "Property Images") {
            dispatch(resetPropertyImagesAction());
          }

          loadDocumentData(sectionName);
          dispatch(changeStatus(true));
          setTimeout(() => {
            setToastMessageOpen(false);
          }, 4000);
          handleAccordianChange(sectionName);
        } else {
          setSubmitLoaderButton(false);
          setToastMessageOpen(true);
          setTimeout(() => {
            setToastMessageOpen(false);
          }, 4000);
        }
      } else {
        setSubmitLoaderButton(false);
        setIsSectionValueEmpty(true);
        setTimeout(() => {
          setIsSectionValueEmpty(false);
        }, 4000);
      }
    } catch (error: any) {
      setSubmitLoaderButton(false);
      setToastMessageOpen(true);
      setToastMessage({
        message: error.message || "Upload failed",
        severity: "error",
      });
      setTimeout(() => {
        setToastMessageOpen(false);
      }, 4000);
      dispatch(changeStatus(true));
      loadDocumentData(sectionName);
    }
  };

  const [expanded, setExpanded] = React.useState<string | false>("panel1");

  const loadDocumentData = async (panel: string) => {
    await documentServices
      .getDocumentsBySectionAndAssetPlanId({
        section: panel,
        assetPlanId: assetPlanId,
      })
      .then((data: any) => {
        setDocumentData(data.documents);
      });
  };

  const handleAccordianChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };
  //logic to create new user...

  useEffect(() => {
    const sectionKey =
      dapData?.internationalInd === "Y"
        ? "GlobalDocumentSections"
        : "USDocumentSections";
    console.log("Fetching sections with key:", sectionKey);
    dispatch(fetchDocumentSections(sectionKey));
  }, [dispatch, dapData?.internationalInd]);

  const HandleSelectChange = (event: SelectChangeEvent) => {
    setSectionValue(event.target.value);
  };

  return (
    <div className="App document-wrap">
      <div className="doc-section-width">
        {toastMessageOpen && (
          <GenericToast
            message={toastMessage.message}
            severity={toastMessage.severity}
          />
        )}
        {isSectionValueEmpty && (
          <GenericToast
            message="Please Select Section From Dropdown"
            severity="error"
          />
        )}

        {(props.mode == "edit" || (dapData?.status?.toLowerCase() === "approved" && (isAnalyst || isAssetManager || isAdmin ))) && (
          <div className="select-tab">
              <div className="doc-select-box">
                <label>Select Tab</label>
                <FormControl sx={{ m: 1, minWidth: 300 }}>
                  <Select
                    labelId="demo-simple-select-helper-label"
                    id="demo-simple-select-helper"
                    onChange={HandleSelectChange}
                    defaultValue=""
                    disabled={documentSectionsLoading}
                  >
                    {documentSections.map((section) => (
                      <MenuItem key={section} value={section}>
                        {section}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              {dapData?.investmentId.toLowerCase().includes("portfolio") && (sectionValue ==="Property Images") &&(
                <div className="doc-select-box">
                  <label>Investment Id</label>
                  <FormControl sx={{ m: 1, minWidth: 300 }}>
                    <Select
                      labelId="demo-simple-select-helper-label-investment"
                      id="demo-simple-select-helper-investment"
                      onChange={handleInvestmentIdChange}
                      value={selectedInvestmentId}
                      disabled={documentSectionsLoading}
                    >
                      {portfolioInvestments.map((investment: any) => (
                        <MenuItem
                          key={investment.investmentId}
                          value={investment.investmentId}
                        >
                          {investment.investmentId}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            {(sectionValue === "SitePlan" ||
              sectionValue === "StackingPlan") && (
              <span id="upload-image">Upload only images in this section</span>
            )}

            <label htmlFor="contained-button-file">
              <Input
                accept="image/*"
                id="contained-button-file"
                multiple
                type="file"
              />
            </label>
            <div>
              <label>Upload File</label>
              <p className="file-validation-msg">
                *Filename should not contain any special character except _ or -
              </p>
              <ReactFileUpload
                accept=".jpg,.png,.jpeg,.xlsx,.pdf"
                multiple
                updateFilesCb={updateUploadedFiles}
                sectionValue={sectionValue}
              />
              <LoadingButton
                disabled={
                  newUserInfo.profileImages.length === 0 ||
                  sectionValue === undefined ||
                  (dapData?.investmentId.toLowerCase().includes("portfolio") && sectionValue === "Property Images" && !selectedInvestmentId)
                }
                className={
                  newUserInfo.profileImages.length === 0 ||
                  sectionValue === undefined ||
                  (dapData?.investmentId.toLowerCase().includes("portfolio") && sectionValue === "Property Images" && !selectedInvestmentId)
                    ? "disabled-btn"
                    : "primary"
                }
                loading={submitLoaderButton}
                onClick={handleSubmit}
                variant="contained"
                component="span"
              >
                Upload Documents
              </LoadingButton>
            </div>
          </div>
        )}

        {documentSections.map((section) => (
          <Accordion
            key={section}
            expanded={expanded === section}
            onChange={handleAccordianChange(section)}
          >
            <AccordionSummary
              aria-controls={`${section}-content`}
              id={`${section}-header`}
            >
              <Typography>{section}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <DocumentComponent
                sectionName={section}
                assetPlanId={assetPlanId}
                document={documentData}
                mode={props.mode}
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    </div>
  );
}

export default Documents;
